import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Badge
} from '@chakra-ui/react'
import { FiArrowLeft, FiCheckCircle, FiInfo, FiCheck } from 'react-icons/fi'
import { FaFacebook, FaGoogle } from 'react-icons/fa'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { PackCard } from '../../components/checkout/PackCard'
import { CreativeSummary } from '../../components/checkout/CreativeSummary'
import { StripeCheckoutForm } from '../../components/checkout/StripeCheckoutForm'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { useSpotlightPacks } from '../../hooks/useSpotlightPacks'
import { useSpotlightCheckout } from '../../hooks/useSpotlightCheckout'
import { createToaster, Toaster } from '@chakra-ui/react'
import { apiConfig } from '../../config/api'
import { formatErrorForUser, logError, notifySupport } from '../../utils/errorHandling'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

export const PackageSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { packs, isLoading: isLoadingPacks, error: packsError } = useSpotlightPacks()
  const { createPurchase, isProcessing: isProcessingPurchase, error: purchaseError } = useSpotlightCheckout()
  const { basic_info, generated_assets } = useOnboarding()

  const [selectedPackId, setSelectedPackId] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [selectedPack, setSelectedPack] = useState<typeof packs[0] | null>(null)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['meta'])
  const [isReadyForPayment, setIsReadyForPayment] = useState(false)
  const [packDistributionMode, setPackDistributionMode] = useState<'split' | 'separate'>('split')

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        // Don't allow deselecting if it's the only platform
        if (prev.length === 1) return prev
        return prev.filter(p => p !== platform)
      }
      return [...prev, platform]
    })
    // If payment was already initiated, reset it so user can change platforms
    if (clientSecret) {
      setClientSecret(null)
      setIsReadyForPayment(false)
    }
  }

  // Get path prefix from current route
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding' // Default fallback
  }
  const pathPrefix = getPathPrefix()

  // Determine popular pack (25k impressions)
  const popularPackId = packs.find(p => p.impressions === 25000)?.id || null

  const handleCreatePurchase = async (packId: string) => {
    try {
      const isSeparatePacks = packDistributionMode === 'separate' && selectedPlatforms.length === 2
      
      const response = await createPurchase(
        packId, 
        basic_info, 
        generated_assets, 
        selectedPlatforms,
        isSeparatePacks
      )
      setClientSecret(response.client_secret)
    } catch (err) {
      logError(err, 'Payment initialization')
      const userMessage = formatErrorForUser(err, 'Payment initialization')
      toaster.create({
        render: () => (
          <Box>
            <Text fontWeight="semibold">Payment initialization failed</Text>
            <Text fontSize="sm" color="gray.600">{userMessage}</Text>
          </Box>
        ),
        type: 'error',
        duration: 7000
      })
      setSelectedPackId(null)
      setSelectedPack(null)
    }
  }

  // When pack is selected, set the selected pack
  useEffect(() => {
    if (selectedPackId && packs.length > 0) {
      const pack = packs.find(p => p.id === selectedPackId)
      if (pack) {
        setSelectedPack(pack)
      }
    }
  }, [selectedPackId, packs])

  // Create purchase intent when user is ready for payment
  useEffect(() => {
    if (isReadyForPayment && selectedPackId && !clientSecret && !isProcessingPurchase && packs.length > 0) {
      const pack = packs.find(p => p.id === selectedPackId)
      if (pack) {
        handleCreatePurchase(pack.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyForPayment, selectedPackId, clientSecret, isProcessingPurchase, packs.length])

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    // Navigate to confirmation page immediately after payment success
    // The confirmation page will handle waiting for ad platform API responses
    navigate(`${pathPrefix}/confirmation`, {
      state: {
        paymentIntentId,
        paymentSuccess: true
      }
    })
  }

  const handlePaymentError = (error: string) => {
    logError(new Error(error), 'Payment processing')
    const userMessage = formatErrorForUser(new Error(error), 'Payment processing')
    toaster.create({
      render: () => (
        <Box>
          <Text fontWeight="semibold">Payment failed</Text>
          <Text fontSize="sm" color="gray.600">{userMessage}</Text>
        </Box>
      ),
      type: 'error',
      duration: 7000
    })
  }

  const handleBack = () => {
    navigate(`${pathPrefix}/targeting`)
  }

  return (
    <OnboardingLayout
      currentStep={5}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Landing page', 'Targeting', 'Select package']}
    >
      <VStack gap={8} align="stretch" maxW="6xl" mx="auto" px={{ base: 4, sm: 6, lg: 8 }}>
        {/* Header */}
        <VStack gap={4} textAlign="center">
          <Box
            bg="green.50"
            borderWidth="1px"
            borderColor="green.200"
            borderRadius="full"
            px={4}
            py={2}
            display="inline-flex"
            alignItems="center"
            gap={2}
          >
            <Icon as={FiCheckCircle} color="green.600" />
            <Text fontSize="sm" fontWeight="semibold" color="green.700">
              Your carousel is ready — select a package to continue.
            </Text>
          </Box>
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            Select Your Visibility Pack
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            You're one step away. Choose a visibility pack and ad platforms — we'll create your campaigns as paused drafts. Activate from your dashboard when ready.
          </Text>
        </VStack>

        {/* Summary Card */}
        <CreativeSummary />

        {/* Error Display */}
        {packsError && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="md"
            color="red.700"
          >
            <Text fontSize="sm">{packsError}</Text>
          </Box>
        )}

        {purchaseError && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="md"
            color="red.700"
          >
            <Text fontSize="sm">{purchaseError}</Text>
          </Box>
        )}

        {/* Pack Selection */}
        {isLoadingPacks ? (
          <Box textAlign="center" py={20}>
            <Text fontSize="lg" color="gray.600">Loading packs...</Text>
          </Box>
        ) : packs.length > 0 ? (
          <VStack gap={8} align="stretch">
            <Box>
              <Heading size="xl" textAlign="center" mb={8}>
                Choose Your Visibility Pack
              </Heading>
              <Text fontSize="lg" color="gray.600" textAlign="center" mb={12} maxW="2xl" mx="auto">
                Pay per pack activation. No subscriptions. Activate packs whenever you need more visibility.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
                {packs.map((pack) => (
                  <PackCard
                    key={pack.id}
                    pack={pack}
                    isSelected={selectedPackId === pack.id}
                    isPopular={pack.id === popularPackId}
                    onSelect={setSelectedPackId}
                    disabled={isProcessingPurchase || !!clientSecret}
                  />
                ))}
              </SimpleGrid>
            </Box>

            {/* Platform Selection */}
            {selectedPackId && (
              <Card.Root borderColor="brand.200" borderWidth="2px" bg="white">
                <Card.Body p={6}>
                  <VStack gap={4} align="stretch">
                    <VStack gap={1} align="start">
                      <Heading size="md" color="gray.900">
                        Where should we run your ads?
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Select one or both platforms. Your impressions will be distributed across selected platforms.
                      </Text>
                    </VStack>
                    
                    <HStack gap={4} flexWrap="wrap">
                      {/* Meta Option */}
                      <Box
                        as="button"
                        onClick={() => togglePlatform('meta')}
                        p={4}
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor={selectedPlatforms.includes('meta') ? 'blue.500' : 'gray.200'}
                        bg={selectedPlatforms.includes('meta') ? 'blue.50' : 'white'}
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ borderColor: selectedPlatforms.includes('meta') ? 'blue.600' : 'gray.300' }}
                        position="relative"
                        minW="180px"
                        disabled={!!clientSecret}
                        opacity={clientSecret ? 0.6 : 1}
                      >
                        <HStack gap={3}>
                          <Icon as={FaFacebook} boxSize={6} color="blue.600" />
                          <VStack align="start" gap={0}>
                            <Text fontWeight="semibold" color="gray.900">Meta</Text>
                            <Text fontSize="xs" color="gray.500">Facebook & Instagram</Text>
                          </VStack>
                        </HStack>
                        {selectedPlatforms.includes('meta') && (
                          <Box position="absolute" top={2} right={2}>
                            <Icon as={FiCheck} color="blue.600" boxSize={4} />
                          </Box>
                        )}
                      </Box>

                      {/* Google Ads Option */}
                      <Box
                        as="button"
                        onClick={() => togglePlatform('google')}
                        p={4}
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor={selectedPlatforms.includes('google') ? 'red.500' : 'gray.200'}
                        bg={selectedPlatforms.includes('google') ? 'red.50' : 'white'}
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ borderColor: selectedPlatforms.includes('google') ? 'red.600' : 'gray.300' }}
                        position="relative"
                        minW="180px"
                        disabled={!!clientSecret}
                        opacity={clientSecret ? 0.6 : 1}
                      >
                        <HStack gap={3}>
                          <Icon as={FaGoogle} boxSize={6} color="red.500" />
                          <VStack align="start" gap={0}>
                            <Text fontWeight="semibold" color="gray.900">Google Ads</Text>
                            <Text fontSize="xs" color="gray.500">Search & Display</Text>
                          </VStack>
                        </HStack>
                        {selectedPlatforms.includes('google') && (
                          <Box position="absolute" top={2} right={2}>
                            <Icon as={FiCheck} color="red.500" boxSize={4} />
                          </Box>
                        )}
                      </Box>
                    </HStack>

                    {selectedPlatforms.length === 2 && (
                      <VStack gap={4} align="stretch" mt={2}>
                        <Box bg="purple.50" p={4} borderRadius="md" borderWidth="1px" borderColor="purple.200">
                          <VStack gap={3} align="stretch">
                            <Text fontSize="sm" fontWeight="semibold" color="purple.900">
                              Choose how to distribute your impressions:
                            </Text>
                            
                            {/* Split Pack Option */}
                            <Box
                              as="button"
                              onClick={() => setPackDistributionMode('split')}
                              p={4}
                              borderRadius="md"
                              borderWidth="2px"
                              borderColor={packDistributionMode === 'split' ? 'purple.500' : 'gray.200'}
                              bg={packDistributionMode === 'split' ? 'purple.50' : 'white'}
                              textAlign="left"
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{ borderColor: packDistributionMode === 'split' ? 'purple.600' : 'gray.300' }}
                            >
                              <HStack justify="space-between" mb={2}>
                                <Text fontWeight="semibold" color="gray.900">
                                  Split Pack (50/50)
                                </Text>
                                {packDistributionMode === 'split' && (
                                  <Icon as={FiCheck} color="purple.600" boxSize={5} />
                                )}
                              </HStack>
                              <Text fontSize="sm" color="gray.600">
                                Your {selectedPack?.impressions.toLocaleString()} impressions will be split evenly between Meta and Google Ads.
                              </Text>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                ${((selectedPack?.price_cents || 0) / 100).toFixed(0)} total
                              </Text>
                            </Box>

                            {/* Separate Packs Option - Upsell */}
                            <Box
                              as="button"
                              onClick={() => setPackDistributionMode('separate')}
                              p={4}
                              borderRadius="md"
                              borderWidth="2px"
                              borderColor={packDistributionMode === 'separate' ? 'green.500' : 'gray.200'}
                              bg={packDistributionMode === 'separate' ? 'green.50' : 'white'}
                              textAlign="left"
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{ borderColor: packDistributionMode === 'separate' ? 'green.600' : 'gray.300' }}
                              position="relative"
                            >
                              <Badge
                                position="absolute"
                                top={2}
                                right={2}
                                colorPalette="green"
                                variant="solid"
                                fontSize="xs"
                              >
                                RECOMMENDED
                              </Badge>
                              <HStack justify="space-between" mb={2}>
                                <VStack align="start" gap={0}>
                                  <Text fontWeight="semibold" color="gray.900">
                                    Separate Packs (One for Each)
                                  </Text>
                                  <Text fontSize="xs" color="green.700" fontWeight="medium">
                                    Better performance & control
                                  </Text>
                                </VStack>
                                {packDistributionMode === 'separate' && (
                                  <Icon as={FiCheck} color="green.600" boxSize={5} />
                                )}
                              </HStack>
                              <VStack align="start" gap={2} mt={2}>
                                <Text fontSize="sm" color="gray.600">
                                  Purchase a dedicated pack for each platform:
                                </Text>
                                <VStack align="start" gap={1} pl={3}>
                                  <HStack gap={2}>
                                    <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="gray.700">
                                      <strong>Full impressions per platform</strong> - No dilution, maximum reach on each
                                    </Text>
                                  </HStack>
                                  <HStack gap={2}>
                                    <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="gray.700">
                                      <strong>Platform-specific optimization</strong> - Each platform optimizes independently
                                    </Text>
                                  </HStack>
                                  <HStack gap={2}>
                                    <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="gray.700">
                                      <strong>Better performance tracking</strong> - Clear metrics per platform
                                    </Text>
                                  </HStack>
                                  <HStack gap={2}>
                                    <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="gray.700">
                                      <strong>Independent campaign control</strong> - Pause/adjust each platform separately
                                    </Text>
                                  </HStack>
                                </VStack>
                                <Box mt={2} p={2} bg="green.100" borderRadius="md" w="full">
                                  <Text fontSize="sm" fontWeight="semibold" color="green.900">
                                    ${((selectedPack?.price_cents || 0) * 2 / 100).toFixed(0)} total ({selectedPack?.impressions.toLocaleString()} × 2 = {(selectedPack?.impressions || 0) * 2} total impressions)
                                  </Text>
                                </Box>
                              </VStack>
                            </Box>
                          </VStack>
                        </Box>
                      </VStack>
                    )}

                    {/* Continue to Payment Button */}
                    {!clientSecret && (
                      <VStack gap={2} w="full">
                        <Button
                          size="lg"
                          colorPalette="brand"
                          onClick={() => setIsReadyForPayment(true)}
                          isLoading={isProcessingPurchase}
                          disabled={isProcessingPurchase}
                          w="full"
                        >
                          Continue to Payment
                        </Button>
                        {selectedPlatforms.length === 2 && packDistributionMode === 'separate' && (
                          <Text fontSize="xs" color="gray.500" textAlign="center">
                            You'll be charged ${((selectedPack?.price_cents || 0) * 2 / 100).toFixed(0)} for two separate packs
                          </Text>
                        )}
                      </VStack>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {/* Payment Form */}
            {selectedPack && clientSecret && (
              <Card.Root borderColor="brand.200" borderWidth="2px" bg="white">
                <Card.Body p={8}>
                  <VStack gap={6} align="stretch">
                    <VStack gap={2} align="start">
                      <Heading size="lg" color="gray.900">
                        Complete Payment
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Enter your payment details to activate your {selectedPack.name}.
                      </Text>
                    </VStack>
                    <StripeCheckoutForm
                      clientSecret={clientSecret}
                      amount={
                        selectedPlatforms.length === 2 && packDistributionMode === 'separate'
                          ? selectedPack.price_cents * 2
                          : selectedPack.price_cents
                      }
                      packName={
                        selectedPlatforms.length === 2 && packDistributionMode === 'separate'
                          ? `${selectedPack.name} × 2 (Separate Packs)`
                          : selectedPack.name
                      }
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {/* How It Works */}
            <Box bg="gray.50" p={6} borderRadius="lg" w="full" maxW="3xl" mx="auto" textAlign="center">
              <HStack justify="center" gap={2} mb={2}>
                <Icon as={FiInfo} color="gray.500" />
                <Text fontWeight="bold" color="gray.700">How It Works</Text>
              </HStack>
              <VStack align="start" gap={2} textAlign="left" maxW="md" mx="auto">
                <Text fontSize="sm" color="gray.600">
                  • <strong>Pay per pack:</strong> Activate visibility packs only when you need them
                </Text>
                <Text fontSize="sm" color="gray.600">
                  • <strong>No subscriptions:</strong> No monthly fees, no commitments
                </Text>
              </VStack>
            </Box>
          </VStack>
        ) : (
          <Card.Root borderColor="gray.200">
            <Card.Body p={8} textAlign="center">
              <VStack gap={3}>
                <Text fontSize="md" color="gray.800" fontWeight="medium">
                  No packs available
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Please contact support if you believe this is an error.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Action Buttons */}
        <HStack gap={4} justify="space-between" pt={4} w="full">
          <Button
            variant="ghost"
            onClick={handleBack}
          >
            <Icon as={FiArrowLeft} />
            Back (edit targeting)
          </Button>
        </HStack>
      </VStack>
      <Toaster toaster={toaster}>{() => null}</Toaster>
    </OnboardingLayout>
  )
}
